/**
 * Soft target — who an attack press or an ability press is aimed at
 * (docs/design/combat.md §Control model, "Soft target"; combat-depth.md §3.2).
 *
 * Why it exists (2026-10-21, measured while measuring the guest demo, AD-794):
 * the HUD sent `abilityIntent` with NO `targetEntityId`, so every damage ability
 * spent stamina + cooldown and did nothing (`zone.tryUseAbility`: no target ⇒
 * the damage effect breaks); and the basic attack faced only the last walk
 * direction, so a skirmisher holding at 5–8 m answered every F with "out of
 * range". The server already validates a named target (26 m bound) and already
 * resolves the swing (3 m reach, ±66° front arc) — no protocol change: the client
 * just names the right enemy, faces it, and closes the distance.
 *
 * Pure (no THREE, no DOM) so it runs under `node --test`. The tunables below are
 * client presentation choices, not combat numbers: nothing here changes damage,
 * reach, cooldowns or aggro — the server stays the only judge of every hit.
 */

import { pointInZone, type TelegraphShape } from '@great-cloud/shared/client';

import { describeTelegraph } from './telegraphGeometry.js';

/** Nearest-hostile fallback radius, metres — the enemy aggro radius (server `ENEMY_AGGRO_RANGE_M`). */
export const SOFT_TARGET_RADIUS_M = 12;
/**
 * How far a hostile that threatened you may still be picked, metres — the server's
 * ability target bound (`ABILITY_MAX_TARGET_RANGE_M` = the leash). Beyond it a named
 * target is refused, so picking it would only waste the press.
 */
export const SOFT_TARGET_THREAT_RANGE_M = 26;
/** How long a telegraph that covered you, or a blow that hit you, marks its source, ms. */
export const THREAT_MEMORY_MS = 4000;
/**
 * Swing when the soft target is this close, metres — inside the server's 3 m reach
 * (`PLAYER_ATTACK_RANGE_M`) with margin for a target that steps back in flight.
 */
export const MELEE_SWING_RANGE_M = 2.6;
/** The server's basic-attack reach, metres (`PLAYER_ATTACK_RANGE_M`) — mirrored for the readiness check. */
export const SERVER_ATTACK_REACH_M = 3;
/**
 * Front-arc dot the client waits for before sending the swing — stricter than the
 * server's 0.4 (`PLAYER_ATTACK_ARC_DOT`, ±66°) so a facing that lands on the edge
 * is not refused as `noTarget`.
 */
export const SWING_READY_ARC_DOT = 0.6;
/** A walk-to-swing gives up after this long, ms (target kited away, path blocked). */
export const ATTACK_APPROACH_TIMEOUT_MS = 5000;

export interface Point {
  x: number;
  z: number;
}

/** The parts of an entity the picker reads. `health` absent ⇒ treated as alive. */
export interface HostileView {
  id: number;
  x: number;
  z: number;
  health?: number;
}

/**
 * Which hostiles recently threatened the player: source id → last threat time.
 * Fed from the wire events the client already receives (`telegraph`,
 * `telegraphResolve`, `attack`); read by `pickSoftTarget`.
 */
export class ThreatMemory {
  private readonly last = new Map<number, number>();

  /** A telegraph began: it marks its source when its shape covers the player. */
  noteTelegraph(sourceId: number, shape: TelegraphShape, self: Point | null, nowMs: number): void {
    if (self === null) return;
    if (telegraphCovers(shape, self)) this.mark(sourceId, nowMs);
  }

  /** A telegraph landed: it marks its source when the player was among the hit. */
  noteResolve(sourceId: number, hits: readonly number[], selfId: number, nowMs: number): void {
    if (hits.includes(selfId)) this.mark(sourceId, nowMs);
  }

  /** A direct blow (`attack` event) at the player marks its source. */
  noteAttack(sourceId: number, targetId: number | null, selfId: number, nowMs: number): void {
    if (targetId === selfId && sourceId !== selfId) this.mark(sourceId, nowMs);
  }

  mark(sourceId: number, nowMs: number): void {
    this.last.set(sourceId, nowMs);
  }

  /** Last threat time for `id`, or undefined. */
  at(id: number): number | undefined {
    return this.last.get(id);
  }

  /** Forget everything (a zone swap renumbers every id). */
  clear(): void {
    this.last.clear();
  }

  /** Drop entries older than the memory window (bounded growth). */
  prune(nowMs: number): void {
    for (const [id, t] of this.last) if (nowMs - t > THREAT_MEMORY_MS) this.last.delete(id);
  }
}

/** Whether a wire telegraph shape covers `self` — the server's own hit-test (`pointInZone`). */
export function telegraphCovers(shape: TelegraphShape, self: Point): boolean {
  // describeTelegraph builds the zone at the origin; 4 segments — only the zone is read.
  const zone = describeTelegraph(shape, 4).zone;
  return pointInZone(zone, self.x - shape.x, self.z - shape.z);
}

export interface SoftTargetQuery {
  self: Point | null;
  hostiles: Iterable<HostileView>;
  threats: ThreatMemory;
  nowMs: number;
  /** An explicit target the player chose (a target click/tap). Reserved — none is set yet. */
  explicitTargetId?: number | null;
}

export interface SoftTargetPick {
  id: number;
  x: number;
  z: number;
  distance: number;
  reason: 'explicit' | 'threat' | 'nearest';
}

function alive(h: HostileView): boolean {
  return h.health === undefined || h.health > 0;
}

/**
 * The soft target (combat.md §Control model): an explicit target if one is held
 * and still legal; else the nearest live hostile that threatened the player within
 * `THREAT_MEMORY_MS` and `SOFT_TARGET_THREAT_RANGE_M`; else the nearest live hostile
 * within `SOFT_TARGET_RADIUS_M`; else null.
 */
export function pickSoftTarget(q: SoftTargetQuery): SoftTargetPick | null {
  const self = q.self;
  if (self === null) return null;
  let threat: SoftTargetPick | null = null;
  let near: SoftTargetPick | null = null;
  let explicit: SoftTargetPick | null = null;
  for (const h of q.hostiles) {
    if (!alive(h)) continue;
    const distance = Math.hypot(h.x - self.x, h.z - self.z);
    if (q.explicitTargetId != null && h.id === q.explicitTargetId) {
      if (distance <= SOFT_TARGET_THREAT_RANGE_M)
        explicit = { id: h.id, x: h.x, z: h.z, distance, reason: 'explicit' };
      continue;
    }
    const t = q.threats.at(h.id);
    if (
      t !== undefined &&
      q.nowMs - t <= THREAT_MEMORY_MS &&
      distance <= SOFT_TARGET_THREAT_RANGE_M &&
      (threat === null || distance < threat.distance)
    ) {
      threat = { id: h.id, x: h.x, z: h.z, distance, reason: 'threat' };
    }
    if (distance <= SOFT_TARGET_RADIUS_M && (near === null || distance < near.distance)) {
      near = { id: h.id, x: h.x, z: h.z, distance, reason: 'nearest' };
    }
  }
  return explicit ?? threat ?? near;
}

/** Heading from `from` to `to` in the movement convention (`atan2(dx, dz)`; 0 = +z). */
export function headingTo(from: Point, to: Point): number {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

/** What one attack press should do, given the soft target. */
export type AttackPlan =
  /** No hostile in the radius: send the press as-is — the server's refusal is the truthful answer. */
  | { kind: 'send' }
  /** In reach: face the target, then swing. */
  | { kind: 'swing'; targetId: number; facing: number }
  /** Out of reach: walk to the target, swing on arrival. */
  | { kind: 'approach'; targetId: number; facing: number };

export function planAttack(self: Point | null, pick: SoftTargetPick | null): AttackPlan {
  if (self === null || pick === null) return { kind: 'send' };
  const facing = headingTo(self, pick);
  return pick.distance <= MELEE_SWING_RANGE_M
    ? { kind: 'swing', targetId: pick.id, facing }
    : { kind: 'approach', targetId: pick.id, facing };
}

/**
 * Whether the SERVER would resolve a swing from `server` (its last acknowledged
 * position + facing) onto `target` — reach and the front arc, the same test as
 * `zone.tryAttack`, with the stricter `SWING_READY_ARC_DOT`. The swing is sent only
 * once this holds, because the attack intent is resolved on receipt while the facing
 * input is integrated on the next tick.
 */
export function swingReady(
  server: { x: number; z: number; facing: number },
  target: Point,
): boolean {
  const dx = target.x - server.x;
  const dz = target.z - server.z;
  const dist = Math.hypot(dx, dz);
  if (dist > SERVER_ATTACK_REACH_M) return false;
  if (dist <= 0.001) return true;
  const dot = (Math.sin(server.facing) * dx + Math.cos(server.facing) * dz) / dist;
  return dot >= SWING_READY_ARC_DOT;
}

/** The step a walk-to-swing takes this frame. */
export type ApproachStep =
  | { kind: 'abandon'; why: 'gone' | 'timeout' | 'tooFar' }
  | { kind: 'walk'; x: number; z: number }
  | { kind: 'face'; facing: number; swing: boolean };

/**
 * Advance a pending attack (pure): abandon when the target is gone/dead, too far or
 * the approach timed out; walk while outside `MELEE_SWING_RANGE_M`; inside it, stop,
 * face, and swing once the server-acknowledged state agrees (`swingReady`).
 */
export function stepApproach(args: {
  self: Point;
  server: { x: number; z: number; facing: number };
  target: HostileView | undefined;
  startedMs: number;
  nowMs: number;
}): ApproachStep {
  const { self, server, target, startedMs, nowMs } = args;
  if (target === undefined || !alive(target)) return { kind: 'abandon', why: 'gone' };
  if (nowMs - startedMs > ATTACK_APPROACH_TIMEOUT_MS) return { kind: 'abandon', why: 'timeout' };
  const distance = Math.hypot(target.x - self.x, target.z - self.z);
  if (distance > SOFT_TARGET_THREAT_RANGE_M) return { kind: 'abandon', why: 'tooFar' };
  if (distance > MELEE_SWING_RANGE_M) return { kind: 'walk', x: target.x, z: target.z };
  return { kind: 'face', facing: headingTo(self, target), swing: swingReady(server, target) };
}
